import React, { useMemo, useState } from 'react';
import { Button, Table, Typography, AutoComplete, Checkbox } from 'antd';
import { PlusOutlined } from '@ant-design/icons'
import { DeleteIcon, EditIcon, CheckIcon, CircleCrossIcon } from 'components/Icons'
import AuthorisedContainer from 'components/AuthorisedContainer';
import { PermissionSetMap } from 'constants/RBACConstants';
import { getVocabulary } from './Vocabulary';
import _ from 'utils/lodashUtils';
import useTagTableDetails from 'hooks/useTagTableData';
import { isSuperOrTenantAdmin } from 'utils/authService';

export const ACTION_TYPE = {
    ROW_ADD: 'ROW_ADD_ACTION',
    ROW_EDIT: 'ROW_EDIT_ACTION',
    ROW_EDIT_CANCEL: 'ROW_EDIT_CANCEL_ACTION',
    ROW_SAVE: 'ROW_SAVE_ACTION',
    ROW_DELETE: 'ROW_DELETE_ACTION',
};

function TagTable({ source, staticTags, editable = false, onChange = _.noop, getAllEvents = _.noop, extras, mentionOptions, disableAccessText, tagsRef, preventKeys = [], accountAdminOnlyKeys = [],
    message = "In this view, you can add a key-value pair. Adding only the key without value will be considered as tag.",
    width = "200px",
    className,
    showPartialMatch = false,
    getPartialMatchState,
    onPartialMatchChange,
    ...props
}) {
    const {dataSource, wrapperRef, currentRowRef, tagValue, onAdd, onKeyChange, onKeyInputChange, onDelete, toggleEdit, onSave} = useTagTableDetails({source, onChange, getAllEvents, staticTags, preventKeys, accountAdminOnlyKeys, mentionOptions, tagsRef})
    const [sortInfo, setSortInfo] = useState({ field: null, order: null });

    const sortedDataSource = useMemo(() => {
        const newAddedRecord = dataSource.filter((item) => item.newRecordAdded);
        const oldRecord = dataSource.filter((item) => !item.newRecordAdded);

        if (sortInfo.field && sortInfo.order) {
            oldRecord.sort((a, b) => {
                const aVal = (a[sortInfo.field] || '').toLowerCase();
                const bVal = (b[sortInfo.field] || '').toLowerCase();
                const comparison = aVal.localeCompare(bVal);
                return sortInfo.order === 'ascend' ? comparison : -comparison;
            });
        }
        return [...oldRecord, ...newAddedRecord];
    }, [dataSource, sortInfo])

    const handleTableChange = (p, f, sorter) => {
        setSortInfo({ field: sorter.field, order: sorter.order });
    };

    // Adjust column widths based on whether partial match column is shown
    const keyColumnWidth = showPartialMatch ? '32%' : '42%';
    const valueColumnWidth = showPartialMatch ? '32%' : '41%';

    const columns = useMemo(() => [
        {
            title: 'Key/Tag',
            dataIndex: 'key',
            width: keyColumnWidth,
            ellipsis:true,
            render: (value, currentRecord) => {
                // This is needed in case of sorting, The row gets sort but the row index always starts from 0
                // This calculate the record index from the array it resides
                const tagKeys = tagValue.map(item => item.key);
                const mergedArray = mentionOptions !== undefined ? [...mentionOptions, ...tagKeys] : [...tagKeys];

                if (currentRecord.isEdit) {
                    const rowIndex = _.findIndex(dataSource, (record) => record.id === currentRecord.id);
                    return (
                        <div className='required-mark-after'>
                            <AutoComplete
                                dropdownMatchSelectWidth={false}
                                size={props.size}
                                name={`key_${rowIndex}`}
                                style={{ borderColor: (currentRecord.duplicateError || currentRecord.requiredError || currentRecord.colonError) && '#ff4d4f', width: `${width}`, maxWidth: "100%" }}
                                defaultValue={value}
                                onKeyPress={preventEnter}
                                onChange={(value)=> onKeyChange(value, rowIndex)}
                                filterOption={(inputValue, option) =>
                                option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                                }
                            >{
                                _.map(mergedArray, (option, index) => {
                                    return <AutoComplete.Option key={`${option}_${index}`} value={option}>{option}</AutoComplete.Option>
                                })
                            }</AutoComplete>
                        </div>
                    );
                }
                return value;
            },
            sorter: true
        },
        {
            title: "Value",
            dataIndex: 'value',
            width: valueColumnWidth,
            ellipsis:true,
            render: (value, currentRecord) => {
                // This is needed in case of sorting, The row gets sort but the row index always starts from 0
                // This calculate the record index from the array it resides

                // Use currentRecord.key directly - it's updated in real-time by onKeyChange
                const tagValues = _.flatten(tagValue.filter(record => record.key === currentRecord?.key).map(record => record.value));
                const mergedArray = mentionOptions !== undefined ? [...tagValues,...mentionOptions] : [...tagValues];
                
                if (currentRecord.isEdit) {
                    const rowIndex = _.findIndex(dataSource, (record) => record.id === currentRecord.id);
                    return (
                        <AutoComplete
                            dropdownMatchSelectWidth={false}
                            size={props.size}
                            name={`value_${rowIndex}`}
                            style={{ borderColor: (currentRecord.duplicateError || currentRecord.requiredError || currentRecord.colonError) && '#ff4d4f', width: `${width}`, maxWidth: "100%" }}
                            defaultValue={value}
                            onKeyPress={preventEnter}
                            onChange={(value) => onKeyInputChange(rowIndex, value)}
                        >{
                            _.map(mergedArray, (option, index) => {
                                return <AutoComplete.Option key={`${option}_${index}`} value={option}>{option}</AutoComplete.Option>
                            })
                        }</AutoComplete>
                    );
                }
                return value;
            },
            sorter: true
        },
        ...(showPartialMatch ? [{
            title: "Partial Match",
            dataIndex: 'partialMatch',
            width: '21%',
            align: 'center',
            render: (value, currentRecord) => {
                // Use tag string as key for state lookup - avoids index alignment issues
                // For new rows (newRecordAdded: true), always use ID-based key until saved
                // This ensures the state persists while user is typing
                const tagString = currentRecord.value ? `${currentRecord.key}:${currentRecord.value}` : currentRecord.key;
                const stateKey = currentRecord.newRecordAdded ? `__new__${currentRecord.id}` : tagString;
                const isChecked = getPartialMatchState?.(stateKey) ?? false;
                return (
                    <Checkbox
                        disabled={!currentRecord.isEdit}
                        checked={isChecked}
                        onChange={(e) => onPartialMatchChange?.(stateKey, e.target.checked, currentRecord.id)}
                    />
                );
            },
        }] : []),
        ...(editable ? [{
            title: "Actions",
            align: "center",
            dataIndex: "actions",
            width: '19%',
            render: (value, currentRecord) => {
                // This is needed in case of sorting, The row gets sort but the row index always starts from 0
                // This calculate the record index from the array it resides
                const rowIndex = _.findIndex(dataSource, (record) => record.id === currentRecord.id);
                const isAccountAdminRestricted = _.some(accountAdminOnlyKeys, (key) => currentRecord.key?.startsWith(key)) && !isSuperOrTenantAdmin();
                const isDisabled = currentRecord.disable || isAccountAdminRestricted;
                const permission = isDisabled ? null : PermissionSetMap.UPDATE_FINDING_TAGS;
                const noAccessText = currentRecord.disable
                    ? (disableAccessText || `Cannot Edit - Inherited from ${getVocabulary("product")}/${getVocabulary("subProduct")}/Tool`)
                    : isAccountAdminRestricted
                        ? "Only Account Admins can add/edit/remove armorcode.group tags"
                        : undefined;

                return (
                    currentRecord.isEdit ?
                        <>
                            <AuthorisedContainer permission={permission} tooltip='Cancel Edit' noAccessText={noAccessText}>
                                <CircleCrossIcon className='m-r-xs clickable' onClick={() => toggleEdit(rowIndex, false)} />
                            </AuthorisedContainer>
                            <AuthorisedContainer permission={permission} tooltip='Save Tag' noAccessText={noAccessText}>
                                <CheckIcon className='clickable' onClick={() => onSave(rowIndex)} />
                            </AuthorisedContainer>
                        </>
                        :
                        <>
                            <AuthorisedContainer permission={permission} tooltip='Delete Tag' noAccessText={noAccessText}>
                                <DeleteIcon className='m-r-xs clickable' onClick={() => onDelete(rowIndex)} />
                            </AuthorisedContainer>
                            <AuthorisedContainer permission={permission} tooltip='Edit Tag' noAccessText={noAccessText}>
                                <EditIcon className='clickable' onClick={() => toggleEdit(rowIndex, true)}/>
                            </AuthorisedContainer>
                        </>
                )
            },
        }] : [])
        
    ], [editable, tagValue, mentionOptions, dataSource, props.size, width, onKeyInputChange, disableAccessText, toggleEdit, onSave, onDelete, onKeyChange, accountAdminOnlyKeys, showPartialMatch, getPartialMatchState, onPartialMatchChange, keyColumnWidth, valueColumnWidth]);

    // Track the error state
    const { requiredError, duplicateError, colonError, reservedKeyError, accountAdminOnlyError, lengthError } = currentRowRef.current;

    return (
        <div>
            {
                editable && (
                <div className='m-b-m m-t-sm'>
                    <Typography.Text>
                        {message}
                    </Typography.Text>
                    {extras}
                </div>
                )
            }
            <div ref={wrapperRef} className={`key-value-table ${className} p-t-0`} style={{ background: '#fff' }}>
                <Table
                    dataSource={sortedDataSource}
                    columns={columns}
                    size={"small"}
                    pagination={false}
                    rowKey='id'
                    onChange={handleTableChange}
                    onRow={(record, rowIndex) => ({
                    'data-automation-row-key': rowIndex,
                    })}
                />
                {
                    editable && (
                        <div className='m-t-sm m-l-0 d-flex'>
                            <div
                                className='danger'
                                style={{
                                    width: '70%',
                                }}
                            >
                                {
                                    requiredError ? 
                                        'Please fill all the required field'
                                        : duplicateError ? 
                                        'Please enter unique key and value pair'
                                        : colonError ?
                                        'The field should not contain colon'
                                        : reservedKeyError ?
                                        'This key is reserved'
                                        : accountAdminOnlyError ?
                                        'Only Account Admins can add/edit/remove armorcode.group tags'
                                        : lengthError
                                        ? "key+value length should be less than 996 characters."
                                        : ""
                                }
                            </div>
                            <div style={{ width: '30%' }} className='text-right'>
                                <AuthorisedContainer permission={PermissionSetMap.UPDATE_FINDING_TAGS}>
                                    <Button size='small' type="dashed" icon={<PlusOutlined />} onClick={onAdd}>
                                        Add New
                                    </Button>
                                </AuthorisedContainer>
                            </div>
                        </div>
                    )
                }
            </div>
        </div>
    ); 
}

export default TagTable;

export function filterInvalidRecords(records) {
    return _.filter(records, (record) => !record.newRecordAdded && !record.requiredError && !record.duplicateError && !record.disable);
}

export function preventEnter(e) {
    if ((e.keyCode || e.which) === 13) {
        e.preventDefault();
    }
}

export function lowerCaseStringExceptVariables(str, isVariableSupported) {
    if (!isVariableSupported) {
        return str.toLowerCase();
    }
    let isVariableStart = false;
    return _.reduce(str, (newStr, char, index) => {
        if (char === '$' && str[index + 1] === '{') {
            isVariableStart = true;
        }
        if (isVariableStart && char === '}') {
            isVariableStart = false;
        }
        newStr += isVariableStart ? char : char.toLowerCase();
        return newStr;
    }, '');
}

